import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { PERSONA_IMAGES, MEDDPICC_LABELS, PHASE_INFO, getTrustLevel } from "@/lib/api";
import { 
  Target, 
  TrendingUp, 
  Gauge,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Building2,
  Zap,
  CheckCircle,
  AlertTriangle,
  Info,
} from "lucide-react";

const PERSONA_BRIEFING = {
  jamie_sterling: {
    name: "Jamie Sterling",
    role: "Managing Director, Helios Capital",
    archetype: "Skeptic / Economic Buyer",
    difficulty: "Hard",
    focus: ["Metrics (M)", "Economic Buyer (E)"],
    background: "Former investment banker. Demands quantified ROI, proven track records, and fast timelines. Skeptical of consultants.",
    positive: ["Quantified ROI", "Fast timeline", "Proven track record"],
    negative: ["Vague promises", "Consultant jargon", "Long explanations"]
  },
  evelyn_reed: {
    name: "Dr. Evelyn Reed",
    role: "Operating Partner, Helios Capital",
    archetype: "Champion / Decision Maker",
    difficulty: "Medium",
    focus: ["Decision Criteria (D1)", "Champion (C1)"],
    background: "PhD in Industrial Engineering, former COO. Asks detailed 'how' questions about methodology and risk mitigation.",
    positive: ["Detailed methodology", "Evidence & data", "Acknowledging complexity"],
    negative: ["Oversimplification", "Ignoring expertise", "Lack of due diligence"]
  },
  ben_carter: {
    name: "Ben Carter",
    role: "VP Procurement, Helios Capital",
    archetype: "Blocker / Gatekeeper",
    difficulty: "Very Hard",
    focus: ["Paper Process (P)", "Competition (C2)"],
    background: "Career procurement professional. Will try to commoditize your service and push for discounts.",
    positive: ["Clear pricing", "Contract flexibility", "Risk mitigation"],
    negative: ["Unclear costs", "Rigid terms", "Dismissing procurement"]
  }
};

const SCENARIO_BRIEF = {
  problem: "Innovatech Dynamics is failing its 100-day plan. Supply chain chaos from a botched SensorTech integration is eroding margins by 8%.",
  yourRole: "Senior Partner at AEN. Position as the only logical solution. You are their peer, not a vendor.",
  keyPoints: [
    "Lead with execution, not advice",
    "Speed to impact -- weeks, not months",
    "De-risk with proven track records",
  ]
};

export const MetricsPanel = ({ dealState }) => {
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(true);

  if (!dealState) return null;

  const trustInfo = getTrustLevel(dealState.trust_score);
  const persona = PERSONA_BRIEFING[dealState.active_persona] || PERSONA_BRIEFING.jamie_sterling;
  
  const getMeddpiccColor = (progress) => {
    if (progress >= 0.65) return "bg-emerald-500";
    if (progress >= 0.35) return "bg-amber-400";
    return "bg-slate-200";
  };

  const phases = ["P1", "P2", "P3", "P4"];
  const currentPhaseIndex = phases.indexOf(dealState.phase);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-sm text-slate-900">Deal Metrics</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Phase Progress */}
        <div>
          <p className="data-label mb-3">Phase Progress</p>
          <div className="grid grid-cols-4 gap-1 mb-2">
            {phases.map((phase, index) => {
              const isComplete = index < currentPhaseIndex;
              const isCurrent = phase === dealState.phase;
              const info = PHASE_INFO[phase];
              
              return (
                <div key={phase} className="flex flex-col items-center">
                  <div 
                    className={`w-9 h-9 rounded-full flex items-center justify-center border-2 pointer-events-none ${
                      isComplete ? "bg-emerald-500 border-emerald-500 text-white" :
                      isCurrent ? "bg-primary/10 border-primary text-primary" :
                      "bg-slate-50 border-slate-200 text-slate-300"
                    }`}
                    data-testid={`phase-indicator-${phase}`}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <span className="text-xs font-bold">{phase}</span>
                    )}
                  </div>
                  <span className={`text-[10px] mt-1.5 text-center leading-tight ${isCurrent ? "font-semibold text-slate-800" : "text-slate-400"}`}>
                    {info.name.split(" ")[0]}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="relative h-1 bg-slate-100 rounded-full mt-3">
            <div 
              className="absolute h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${((currentPhaseIndex + (dealState.phase_turn_count > 0 ? 0.5 : 0)) / 4) * 100}%` }}
            />
          </div>
        </div>

        <Separator className="bg-slate-100" />

        {/* Trust Score */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="data-label">Trust Level</p>
            <Badge 
              variant="outline" 
              className={`text-xs ${
                trustInfo.level === "Defensive" ? "bg-red-50 text-red-600 border-red-200" :
                trustInfo.level === "Neutral" ? "bg-yellow-50 text-yellow-600 border-yellow-200" :
                trustInfo.level === "Engaged" ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                "bg-blue-50 text-blue-600 border-blue-200"
              }`}
            >
              {trustInfo.level}
            </Badge>
          </div>
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold text-slate-900" data-testid="trust-score">
                {dealState.trust_score}
              </span>
              <span className="text-xs text-slate-400">/ 100</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  trustInfo.level === "Defensive" ? "bg-red-500" :
                  trustInfo.level === "Neutral" ? "bg-yellow-500" :
                  trustInfo.level === "Engaged" ? "bg-emerald-500" :
                  "bg-blue-500"
                }`}
                style={{ width: `${dealState.trust_score}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-300">
              <span>Defensive</span>
              <span>Advocate</span>
            </div>
          </div>
        </div>

        <Separator className="bg-slate-100" />

        {/* Deal Probability */}
        <div>
          <p className="data-label mb-2">Deal Probability</p>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-slate-900" data-testid="deal-probability">
              {Math.round(dealState.deal_outcome * 100)}%
            </span>
            <TrendingUp className={`w-4 h-4 ${dealState.deal_outcome >= 0.5 ? "text-emerald-500" : "text-amber-400"}`} />
          </div>
        </div>

        <Separator className="bg-slate-100" />

        {/* MEDDPICC Progress */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-3.5 h-3.5 text-primary" />
            <p className="data-label">MEDDPICC Progress</p>
          </div>
          <div className="space-y-3">
            {Object.entries(dealState.meddpicc_progress).map(([key, value]) => {
              const info = MEDDPICC_LABELS[key];
              const percent = Math.round(value * 100);
              
              return (
                <div key={key} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-700 w-5">{key}</span>
                      <span className="text-slate-400">{info?.name}</span>
                    </div>
                    <span className={`font-medium ${
                      percent >= 65 ? "text-emerald-600" :
                      percent >= 35 ? "text-amber-500" :
                      "text-slate-300"
                    }`}>
                      {percent}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 rounded-full ${getMeddpiccColor(value)}`}
                      style={{ width: `${percent}%` }}
                      data-testid={`meddpicc-${key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Separator className="bg-slate-100" />

        {/* Briefing: Scenario & Active Persona */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-3.5 h-3.5 text-primary" />
            <p className="data-label">Briefing</p>
          </div>

          {/* Scenario */}
          <button
            className="w-full flex items-center justify-between mb-2"
            onClick={() => setScenarioOpen(!scenarioOpen)}
            data-testid="metrics-scenario-toggle"
          >
            <div className="flex items-center gap-1.5">
              <Building2 className="w-3 h-3 text-slate-400" />
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Scenario</span>
            </div>
            {scenarioOpen ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
          </button>
          {scenarioOpen && (
            <div className="space-y-2 mb-3 animate-fade-in-up">
              <div className="p-2 bg-orange-50/80 rounded-lg border border-orange-100">
                <p className="text-[10px] font-medium text-orange-700 mb-0.5">Problem</p>
                <p className="text-[10px] text-slate-600 leading-relaxed">{SCENARIO_BRIEF.problem}</p>
              </div>
              <div className="p-2 bg-blue-50/80 rounded-lg border border-blue-100">
                <p className="text-[10px] font-medium text-primary mb-0.5">Your Role</p>
                <p className="text-[10px] text-slate-600 leading-relaxed">{SCENARIO_BRIEF.yourRole}</p>
              </div>
              <ul className="space-y-0.5">
                {SCENARIO_BRIEF.keyPoints.map((pt, i) => (
                  <li key={i} className="flex items-start gap-1 text-[10px] text-slate-500">
                    <Zap className="w-2.5 h-2.5 text-primary mt-0.5 flex-shrink-0" />{pt}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Separator className="bg-slate-100 my-2" />

          {/* Active Persona */}
          <button
            className="w-full flex items-center justify-between mb-2"
            onClick={() => setPersonaOpen(!personaOpen)}
            data-testid="metrics-persona-toggle"
          >
            <div className="flex items-center gap-1.5">
              <Avatar className="w-4 h-4">
                <AvatarImage src={PERSONA_IMAGES[dealState.active_persona]} />
                <AvatarFallback className="text-[6px] bg-slate-100">
                  {persona.name.split(" ").map(n => n[0]).join("")}
                </AvatarFallback>
              </Avatar>
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">{persona.name}</span>
            </div>
            {personaOpen ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
          </button>
          {personaOpen && (
            <div className="space-y-2 animate-fade-in-up">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-slate-400">{persona.archetype}</span>
                <Badge variant="outline" className="text-[9px] py-0 h-3.5 bg-white">{persona.difficulty}</Badge>
              </div>
              <div className="flex flex-wrap gap-1">
                {persona.focus.map((f, i) => (
                  <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{f}</span>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">{persona.background}</p>
              <div className="grid grid-cols-1 gap-1.5">
                <div className="p-1.5 bg-emerald-50/50 rounded-md border border-emerald-100">
                  <p className="text-[9px] font-semibold text-emerald-600 mb-0.5">Builds Trust</p>
                  {persona.positive.map((t, i) => (
                    <p key={i} className="text-[9px] text-slate-500 flex items-center gap-0.5">
                      <CheckCircle className="w-2 h-2 text-emerald-500 flex-shrink-0" />{t}
                    </p>
                  ))}
                </div>
                <div className="p-1.5 bg-red-50/50 rounded-md border border-red-100">
                  <p className="text-[9px] font-semibold text-red-500 mb-0.5">Hurts Trust</p>
                  {persona.negative.map((t, i) => (
                    <p key={i} className="text-[9px] text-slate-500 flex items-center gap-0.5">
                      <AlertTriangle className="w-2 h-2 text-red-400 flex-shrink-0" />{t}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
